import { type FieldMetadata, getInputProps } from "@conform-to/react";
import { Trans, t } from "@lingui/macro";
import {
  PrivateImg,
  type PrivateImgProps,
} from "@winamp/media-upload/BytescaleContext/PrivateImg.js";
import type { Upload } from "@winamp/media-upload/schema.js";
import type { useUploads } from "@winamp/media-upload/use-uploads.js";
import { Button } from "@winamp/ui/components/Button/Button.js";
import {
  type IImageCropperProps,
  ImageCropper,
} from "@winamp/ui/components/CropDialog/ImageCropper.js";
import { ImageCropperContainer } from "@winamp/ui/components/CropDialog/ImageCropperContainer.js";
import { ImageCropperDialog } from "@winamp/ui/components/CropDialog/ImageCropperDialog.js";
import {
  DialogActions,
  DialogBody,
  DialogTitle,
} from "@winamp/ui/components/Dialog/index.js";
import {
  Dropzone,
  DropzoneSubtitle,
  DropzoneTitle,
} from "@winamp/ui/components/Dropzone/index.js";
import { Input } from "@winamp/ui/components/Input/index.js";
import { useToastContext } from "@winamp/ui/components/Toast/use-toast-context.js";
import { classNames } from "@winamp/ui/utils/classNames.js";
import { forwardRef, useImperativeHandle, useState } from "react";
import type { Accept, DropzoneOptions } from "react-dropzone-esm";
import { getDropzoneErrorMessages } from "~/modules/media/get-dropzone-error-messages";
import { MediaUploadStatus } from "./MediaUploadStatus";

interface IImageUploadProps {
  cancelUpload?: ReturnType<typeof useUploads>["cancelUpload"];
  children?:
    | React.ReactNode
    | ((imagePath: string | undefined) => React.ReactNode);
  className?: string;
  crop?: boolean;
  cropProps?: IImageCropperProps;
  disabled?: boolean;
  meta: FieldMetadata<string | null>;
  onFileDrop?: (file: File) => Promise<void>;
  placeholder?: React.ReactNode;
  privateImageOptions?: PrivateImgProps["options"];
  removeUpload?: ReturnType<typeof useUploads>["removeUpload"];
  upload?: Upload;
}

export type ImageUploadHandler = {
  reset: () => void;
};

export const ImageUpload = forwardRef<ImageUploadHandler, IImageUploadProps>(
  (
    {
      cancelUpload,
      children,
      className,
      crop = true,
      cropProps = { aspect: 1, cropShape: "rect" },
      disabled,
      meta,
      onFileDrop,
      placeholder,
      privateImageOptions,
      removeUpload,
      upload,
    },
    ref,
  ) => {
    const { addToast } = useToastContext();

    const [localImage, setLocalImage] = useState<string | undefined>(undefined);
    const [tmpImage, setTmpImage] = useState<{
      file: File;
      url: string;
    } | null>(null);
    const [isCropDialogOpen, setIsCropDialogOpen] = useState(false);

    useImperativeHandle(ref, () => {
      return {
        reset: () => {
          setLocalImage(undefined);
          setTmpImage(null);
        },
      };
    });

    const accept: Accept = {
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "image/tiff": [".tiff", ".tif"],
    };
    const dropzoneOptions: DropzoneOptions = {
      accept,
      onDrop(acceptedFiles, rejectedFiles) {
        if (rejectedFiles.length > 0) {
          const errors = getDropzoneErrorMessages(rejectedFiles, { accept });
          errors.forEach((error) => {
            addToast({
              type: "error",
              title: t`Upload rejected.`,
              message: error,
            });
          });
        }

        if (acceptedFiles?.length) {
          const file = acceptedFiles[0];

          if (crop) {
            setTmpImage({
              file,
              url: URL.createObjectURL(file),
            });
            setIsCropDialogOpen(true);
          } else {
            setLocalImage(URL.createObjectURL(file));
            onFileDrop?.(file);
          }
        }
      },
      disabled: (!!upload && upload.status !== "completed") || disabled,
      multiple: false,
    };

    const onRemoveUpload = (uploadId?: string) => {
      setLocalImage(undefined);
      setTmpImage(null);
      if (uploadId) {
        removeUpload?.(uploadId);
      }
    };

    const onCancelUpload = (uploadId?: string) => {
      setLocalImage(undefined);
      setTmpImage(null);
      if (uploadId) {
        cancelUpload?.(uploadId);
      }
    };
    const imageToUse =
      localImage || meta.value || meta.initialValue || undefined;

    return (
      <>
        <Input
          {...getInputProps(meta, { type: "hidden" })}
          key={meta.key}
          className="hidden"
        />
        <Dropzone
          className={classNames(
            "relative aspect-square max-w-80",
            { "p-0": !!imageToUse },
            className,
          )}
          options={dropzoneOptions}
        >
          {typeof children === "function"
            ? children(imageToUse)
            : (children ??
              (imageToUse ? (
                <PrivateImg
                  alt="Artwork"
                  className="object-cover w-full h-full"
                  src={imageToUse}
                  options={privateImageOptions}
                />
              ) : (
                (placeholder ?? (
                  <div className="flex flex-col gap-1 items-center justify-center">
                    <DropzoneTitle>
                      <Trans>
                        Drag and drop an image or{" "}
                        <span className="text-link font-medium">
                          click to upload
                        </span>
                      </Trans>
                    </DropzoneTitle>
                    <DropzoneSubtitle>
                      <Trans>3000 x 3000 px in PNG, JPG or TIFF</Trans>
                    </DropzoneSubtitle>
                  </div>
                ))
              )))}
          <MediaUploadStatus
            upload={upload}
            removeUpload={onRemoveUpload}
            cancelUpload={onCancelUpload}
          />
        </Dropzone>

        <ImageCropperDialog
          open={isCropDialogOpen}
          onClose={setIsCropDialogOpen}
        >
          <DialogTitle>
            <Trans>Crop your image</Trans>
          </DialogTitle>
          <ImageCropperContainer image={tmpImage?.url ?? ""}>
            {({ context, validateCrop }) => (
              <>
                <DialogBody>
                  <ImageCropper context={context} {...cropProps} />
                </DialogBody>
                <DialogActions>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setIsCropDialogOpen(false);
                      setTmpImage(null);
                    }}
                  >
                    <Trans>Cancel</Trans>
                  </Button>
                  <Button
                    onClick={async () => {
                      const croppedImage = await validateCrop();
                      if (croppedImage) {
                        setIsCropDialogOpen(false);
                        const file = new File(
                          [croppedImage],
                          tmpImage?.file?.name ?? "image.png",
                        );
                        setLocalImage(URL.createObjectURL(file));
                        setTmpImage(null);
                        onFileDrop?.(file);
                      }
                    }}
                  >
                    <Trans>Crop</Trans>
                  </Button>
                </DialogActions>
              </>
            )}
          </ImageCropperContainer>
        </ImageCropperDialog>
      </>
    );
  },
);
